import { useCallback } from 'react';

interface VerifySignatureModalProps {
  open: boolean;
  signatureImageSrc: string;
  onConfirm: () => void;
  onRedo: () => void;
  onClose: () => void;
}

export default function VerifySignatureModal({ open, signatureImageSrc, onConfirm, onRedo, onClose }: VerifySignatureModalProps) {
  // No countdown: the user already vetted this signature when they
  // saved it on /auth/signature-setup. Inside the placement flow we
  // only re-show it as a "this is the image we'll stamp — proceed?"
  // confirmation, which doesn't need a forced wait. The countdown
  // was a hangover from when this modal was reused for the setup
  // flow itself; that path is gone now.

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  if (!open) return null;

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.15)',
      }}
    >
      <div style={{
        width: 520, backgroundColor: '#FEFCF9', border: '1px solid #E0DCD4',
        borderRadius: 16, boxShadow: '#1A17142E 0px 20px 60px',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 28px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1A1714', letterSpacing: '-0.02em' }}>
              Verify your signature
            </h2>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#6B5E52', lineHeight: 1.5 }}>
            Take a moment to confirm this is exactly how you want to sign.
          </p>
        </div>

        {/* Legal notice */}
        <div style={{ padding: '0 28px' }}>
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
            backgroundColor: 'var(--app-bg-sidebar, #F5F2EC)', border: '1px solid var(--app-border, #E8E0D8)', borderRadius: 9,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM8 11V8m0-2.5h.007" stroke="rgba(139,125,107,0.70)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--app-text-secondary, #8B7D6B)', lineHeight: '18px' }}>
              Your e-signature carries the same legal weight as a handwritten signature. Make sure this is correct.
            </p>
          </div>
        </div>

        {/* Signature preview */}
        <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9E9189' }}>
            Your signature
          </p>
          <div style={{
            width: '100%', height: 140, border: '1px solid #E8DED4', borderRadius: 8,
            backgroundColor: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
          }}>
            <img
              src={signatureImageSrc}
              alt="Your signature"
              style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
            />
          </div>
          <div style={{ height: 1.5, backgroundColor: '#D4CAC0' }} />
        </div>

        {/* Buttons */}
        <div style={{
          padding: '16px 28px', display: 'flex', justifyContent: 'flex-end', gap: 10,
          borderTop: '1px solid #E8DED4',
        }}>
          <button
            onClick={onRedo}
            style={{
              border: '1px solid #E4DDD4', borderRadius: 8, padding: '10px 16px',
              backgroundColor: 'transparent', color: '#6B5E52', fontSize: 13, fontWeight: 400,
              cursor: 'pointer', fontFamily: 'Geist, system-ui, sans-serif',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2.5 5.5h5a3 3 0 010 6H5" stroke="#6B5E52" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 3L2.5 5.5 5 8" stroke="#6B5E52" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Redo signature
          </button>
          <button
            onClick={onConfirm}
            style={{
              border: 'none', borderRadius: 8, padding: '10px 20px',
              backgroundColor: '#D97645',
              color: '#FFFFFF', fontSize: 13, fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'Geist, system-ui, sans-serif',
              display: 'flex', alignItems: 'center', gap: 8,
              transition: 'background-color 0.2s ease',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Confirm signature
          </button>
        </div>
      </div>
    </div>
  );
}
